import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Camera,
  Maximize2,
  Sparkles,
  Film,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import WorkshopMediaViewer from "./WorkshopMediaViewer";

const AUTO_PLAY_INTERVAL = 3800; // 3.8 seconds per highlight

/**
 * ============================================================
 * WORKSHOP SESSION HIGHLIGHTS & LAB MOMENTS
 * ============================================================
 * Premium dynamic showcase featuring:
 * - Automatic fluid progression moving highlight assets one by one
 * - Active card expands smoothly with increasing width and height
 * - Inactive cards stay compact and scale gracefully
 * - Interactive controls: Prev / Next, Pause / Resume, and Progress timer
 * - Click-to-view opens in a proper centered Modal Dialog via React Portal
 * - Responsive layout: fluid expanding stage on desktop/tablet, spotlight on mobile
 */
export default function WorkshopGallery({ workshop, highlights = [] }) {
  const [modalIndex, setModalIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressTimerRef = useRef(null);

  const isCompleted = Boolean(
    workshop?.is_completed ||
    workshop?.status === "Completed" ||
    workshop?.lifecycle_stage === "completed_by_schedule"
  );
  const isDraft = workshop?.status === "Draft";
  const isCancelled = workshop?.status === "Cancelled";
  const showHighlights = Boolean(workshop?.show_highlights);

  // Filter to published highlight media only
  const rawList =
    Array.isArray(highlights) && highlights.length > 0
      ? highlights
      : Array.isArray(workshop?.highlights) && workshop?.highlights.length > 0
      ? workshop.highlights
      : [];

  const items = rawList.filter(
    (item) => item && item.media_type === "highlight" && item.is_published !== false
  );

  const total = items.length;

  const handleNext = useCallback(() => {
    if (total <= 1) return;
    setActiveIndex((prev) => (prev + 1) % total);
    setProgress(0);
  }, [total]);

  const handlePrev = useCallback(() => {
    if (total <= 1) return;
    setActiveIndex((prev) => (prev - 1 + total) % total);
    setProgress(0);
  }, [total]);

  // Automatic progression timer with progress animation
  useEffect(() => {
    if (total <= 1 || isPaused || modalIndex !== null) {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      return;
    }

    const startTime = Date.now();
    setProgress(0);

    // Smooth progress bar update (every 40ms)
    progressTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, (elapsed / AUTO_PLAY_INTERVAL) * 100);
      setProgress(pct);
    }, 40);

    // Auto-advance step
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
      setProgress(0);
    }, AUTO_PLAY_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [total, isPaused, modalIndex, activeIndex]);

  // Strictly enforce visibility constraints
  if (isDraft || isCancelled || !showHighlights || items.length === 0) {
    return null;
  }

  const currentItem = items[activeIndex] || items[0];

  return (
    <section
      id="session-highlights"
      aria-labelledby="gallery-heading"
      className="w-full scroll-mt-28 select-none"
    >
      <div
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 sm:p-7 md:p-8 backdrop-blur-xl shadow-[0_18px_50px_rgba(0,0,0,0.35)] relative overflow-hidden"
      >
        {/* Ambient Top Glow */}
        <div className="absolute top-0 left-1/4 right-1/4 h-[1px] bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent pointer-events-none" />

        {/* ── Section Header ───────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <div className="w-10 h-10 rounded-2xl bg-cyan-500/15 border border-cyan-500/25 text-cyan-400 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(6,182,212,0.25)]">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h2
                id="gallery-heading"
                className="text-xl sm:text-2xl font-bold tracking-tight text-white"
              >
                Session Highlights & Lab Moments
              </h2>
              <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                {isCompleted ? "Post-Event Recap" : "Lab Highlights"}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-white/60 mt-0.5">
              {isCompleted
                ? "Archival moments, architecture deep-dives, and code walkthrough snapshots from this completed session."
                : "Architecture deep-dives, lab snapshots, and code walkthrough highlights from this session series."}
            </p>
          </div>
        </div>

        {/* ── Auto-Advance Progress Bar ────────────────────────────── */}
        {total > 1 && (
          <div className="w-full h-1 rounded-full bg-white/5 overflow-hidden mb-5 relative">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300 transition-all duration-75 ease-linear rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* ── Full Width & Height Showcase Stage (Smooth Gliding Animation) ── */}
        <div
          className="relative w-full h-[400px] sm:h-[480px] md:h-[540px] lg:h-[580px] overflow-hidden rounded-2xl sm:rounded-3xl border border-cyan-400/40 bg-black/90 shadow-[0_25px_70px_rgba(6,182,212,0.25)] ring-1 ring-cyan-400/30 my-2 select-none"
          onTouchStart={(e) => {
            if (e.touches && e.touches[0]) {
              timerRef.touchStartX = e.touches[0].clientX;
            }
          }}
          onTouchEnd={(e) => {
            if (timerRef.touchStartX && e.changedTouches && e.changedTouches[0]) {
              const deltaX = timerRef.touchStartX - e.changedTouches[0].clientX;
              if (deltaX > 40) handleNext();
              else if (deltaX < -40) handlePrev();
              timerRef.touchStartX = null;
            }
          }}
        >
          {items.map((item, idx) => {
            // Compute circular relative offset (-1: previous, 0: current, 1: next, etc.)
            let diff = idx - activeIndex;
            while (diff > total / 2) diff -= total;
            while (diff < -total / 2) diff += total;

            const isCurrent = diff === 0;
            const isNext = diff === 1;
            const isPrev = diff === -1;
            const isNearby = isCurrent || isNext || isPrev;

            const isVideo = item.media_format === "video" || Boolean(item.video_url);
            const displayUrl = item.thumbnail_url || item.storage_path || item.url;
            const captionText = item.title || item.caption || `Highlight ${idx + 1}`;

            // Smooth sliding transform: current at 0%, next at 100%, prev at -100%
            const transformStyle = `translateX(${diff * 100}%)`;
            const transitionStyle = isNearby
              ? "transform 900ms cubic-bezier(0.16, 1, 0.3, 1), opacity 400ms ease"
              : "none";
            const opacity = isNearby ? 1 : 0;
            const zIndex = isCurrent ? 20 : 10;

            return (
              <div
                key={item.id || idx}
                data-highlight-id={item.id || idx}
                data-format={isVideo ? "video" : "image"}
                data-is-spotlight={isCurrent ? "true" : "false"}
                onClick={() => {
                  if (isCurrent) {
                    setModalIndex(idx);
                  } else {
                    setActiveIndex(idx);
                    setProgress(0);
                  }
                }}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  transform: transformStyle,
                  transition: transitionStyle,
                  opacity: opacity,
                  zIndex: zIndex,
                  pointerEvents: isCurrent ? "auto" : "none",
                }}
                className="group w-full h-full cursor-pointer select-none overflow-hidden"
              >
                {/* Background Media Image filling full width and height */}
                <img
                  src={displayUrl}
                  alt={item.alt_text || captionText}
                  className="w-full h-full object-cover object-center transition-transform duration-1000 ease-out group-hover:scale-103"
                  loading="lazy"
                />

                {/* Dark Vignette Overlay for Crisp Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/20 transition-colors duration-500 group-hover:from-black/90 group-hover:via-black/30" />

                {/* Top Left Badge: Photo vs Video */}
                <div className="absolute top-4 sm:top-5 left-4 sm:left-6 flex items-center gap-2.5 z-10">
                  {isVideo ? (
                    <span className="px-3 py-1 rounded-full bg-rose-600/90 text-white text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5 backdrop-blur-md shadow-lg">
                      <Film className="w-3.5 h-3.5" />
                      <span>Video</span>
                    </span>
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-black/75 border border-white/20 text-white text-[11px] font-medium tracking-wider uppercase flex items-center gap-1.5 backdrop-blur-md shadow-lg">
                      <Camera className="w-3.5 h-3.5 text-cyan-400" />
                      <span>Photo</span>
                    </span>
                  )}

                  <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-[11px] font-semibold">
                    {activeIndex + 1} of {total}
                  </span>
                </div>

                {/* Top Right: Open in Modal Pill Trigger */}
                <div className="absolute top-4 sm:top-5 right-4 sm:right-6 z-10">
                  <div
                    title="Click to view in full modal"
                    className="px-3.5 py-1.5 rounded-full bg-black/75 border border-cyan-400/40 text-cyan-300 text-xs font-medium flex items-center gap-1.5 backdrop-blur-md shadow-lg hover:bg-cyan-500 hover:text-black transition-colors"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Open in Modal</span>
                  </div>
                </div>

                {/* Bottom Caption Strip with Elevated Typography */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 md:p-8 z-10 bg-gradient-to-t from-black via-black/85 to-transparent pt-12 sm:pt-16">
                  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div className="min-w-0 max-w-3xl space-y-1 sm:space-y-1.5">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-white tracking-tight line-clamp-1">
                        {captionText}
                      </h3>
                      {item.description && (
                        <p className="text-xs sm:text-sm text-white/75 line-clamp-2 leading-relaxed font-normal">
                          {item.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-xs font-semibold text-cyan-300 group-hover:text-cyan-200 transition-colors flex-shrink-0">
                      <span>View Full Viewer</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Slide Pill Indicators & Thumbnails ───────────────────── */}
        {total > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4 sm:mt-5">
            {items.map((it, idx) => (
              <button
                key={it.id || idx}
                type="button"
                onClick={() => {
                  setActiveIndex(idx);
                  setProgress(0);
                }}
                title={it.caption || `Highlight ${idx + 1}`}
                aria-label={`Go to highlight ${idx + 1}`}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300 cursor-pointer",
                  idx === activeIndex
                    ? "w-8 sm:w-10 bg-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.8)]"
                    : "w-2.5 sm:w-3 bg-white/20 hover:bg-white/40"
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Proper Centered Modal Dialog (Mounted via Portal in document.body) ── */}
      <WorkshopMediaViewer
        isOpen={modalIndex !== null}
        items={items}
        initialIndex={modalIndex ?? 0}
        onClose={() => setModalIndex(null)}
      />
    </section>
  );
}
